import type { ConnectionPhase } from '@parsleyai/ui'
import type { FirestoreError, SnapshotMetadata, Unsubscribe } from 'firebase/firestore'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import {
  getConnectionState,
  registerConnectionListener,
  reloadApp,
  reportFailure,
  reportHealthy,
  retryConnection,
  subscribeToConnection
} from '@/services/firebase/connectionRecovery'

// Firestore tears a listener down permanently when its `onSnapshot` error callback fires. This wraps that
// lifecycle so it recovers and keeps last-good data on screen. It owns NO retry policy — a drop is almost
// always client-wide, and that repair lives in `services/firebase/connectionRecovery`.

interface Handlers<T> {
  onData: (snapshot: T) => void
  onError: (error: FirestoreError) => void
}

/** Both `QuerySnapshot` and `DocumentSnapshot` satisfy this — it's how we spot a cache replay. */
interface SnapshotWithMetadata {
  metadata: SnapshotMetadata
}

interface UseLiveSubscriptionParams<T extends SnapshotWithMetadata> {
  /**
   * Resubscribe when this changes; a falsy key disables the subscription (e.g. no signed-in
   * uid). The key is passed back to `subscribe`, so the ref is guaranteed non-null there.
   */
  subscriptionKey: string | null | undefined
  /** Opens the live listener for `key` and returns its unsubscribe. The hook owns when this runs. */
  subscribe: (key: string, handlers: Handlers<T>) => Unsubscribe
  /** Called on every successful snapshot, cached or server-sourced. */
  onData: (snapshot: T) => void
  /** Slice label for diagnostic logging of connection errors (e.g. 'mealPlanResults'). */
  label: string
}

export interface LiveSubscription {
  /** App-wide connection phase — shared by every listener, since they share one transport. */
  phase: ConnectionPhase
  /** Manual recovery — wired to the banner's "Try now". */
  retry: () => void
  /** Terminal escape hatch — wired to the banner's "Reload" once recovery is exhausted. */
  reload: () => void
}

export function useLiveSubscription<T extends SnapshotWithMetadata>({
  subscriptionKey,
  subscribe,
  onData,
  label
}: UseLiveSubscriptionParams<T>): LiveSubscription {
  const { phase, generation } = useSyncExternalStore(subscribeToConnection, getConnectionState)

  // Latest closures held in refs so the subscribe effect can depend ONLY on the key and the
  // recovery generation, not on closure identity — otherwise it would tear down on every render.
  const subscribeRef = useRef(subscribe)
  const onDataRef = useRef(onData)
  const labelRef = useRef(label)
  subscribeRef.current = subscribe
  onDataRef.current = onData
  labelRef.current = label

  // Declared first so the listener is counted before it can report anything.
  useEffect(() => {
    if (!subscriptionKey) return
    return registerConnectionListener()
  }, [subscriptionKey])

  // `generation` is a dependency the body never reads: bumping it is how the recovery module
  // tells every listener to tear down and re-open. Taking Biome's suggested fix here silently
  // disables reconnection — `useLiveSubscription.test.tsx` pins that.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `generation` is the re-subscribe trigger, not a value the effect reads.
  useEffect(() => {
    if (!subscriptionKey) return
    const unsubscribe = subscribeRef.current(subscriptionKey, {
      onData: (snapshot) => {
        onDataRef.current(snapshot)
        // Firestore replays a cached snapshot the instant a listener re-opens, before it has
        // heard a word from the server. Counting that as recovery is what used to pin the
        // backoff at its first step and flash a false "Back online" — only server data counts.
        if (!snapshot.metadata.fromCache) reportHealthy()
      },
      onError: (error) => {
        // Capture the code (not just the message) so idle-session drops can be diagnosed —
        // `unavailable` (dropped WebChannel) vs `permission-denied` (expired token).
        console.error(`[${labelRef.current}] onSnapshot error`, { code: error.code, message: error.message })
        reportFailure(labelRef.current)
      }
    })
    return unsubscribe
  }, [subscriptionKey, generation])

  return { phase, retry: retryConnection, reload: reloadApp }
}
